import { useId } from 'react'
import { cn } from '@/lib/utils'
import { getThemeColor } from '@/shared/hooks/useThemeColors'
import { polarToXY } from './svgMath'

interface AnimatedLogoProps {
  size?: number
  className?: string
}

/**
 * Minimalist animated eye logo for Omni-Sight.
 *
 * Animation sequence:
 *   0.0s — Eye closed (flat line)
 *   0.3s — Eye opens (path morph, 0.6s)
 *   0.9s — Iris ring fades in
 *   1.1s — Pupil fades in, then pulses
 *   1.4s — Data particles begin drifting toward pupil
 */

// Particle definitions: angle (deg), distance from center, duration, delay
const particles = [
  { angle: 30, dist: 14, dur: '3.8s', delay: '1.4s' },
  { angle: 150, dist: 13, dur: '4.2s', delay: '1.8s' },
  { angle: 210, dist: 14, dur: '3.5s', delay: '2.2s' },
  { angle: 330, dist: 13, dur: '4.0s', delay: '1.6s' },
  { angle: 90, dist: 12, dur: '4.5s', delay: '2.5s' },
  { angle: 270, dist: 13, dur: '3.6s', delay: '2.0s' },
] as const

function particleStart(angle: number, dist: number) {
  return polarToXY(16, 16, angle, dist)
}

export function AnimatedLogo({ size = 24, className }: AnimatedLogoProps) {
  const id = useId()

  const strokeColor = getThemeColor('--chart-stroke')
  const pupilFill = getThemeColor('--chart-pupil')
  const glowColor = getThemeColor('--chart-glow-strong')
  const particleColor = getThemeColor('--chart-particle')

  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="none"
      className={cn('animated-logo', className)}
      aria-label="Omni Sight"
    >
      <defs>
        {/* Pupil glow */}
        <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="1.5"
            floodColor={glowColor}
            floodOpacity="1"
          />
        </filter>
        {/* Particle glow */}
        <filter id={`${id}-pglow`} x="-100%" y="-100%" width="300%" height="300%">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="0.8"
            floodColor={particleColor}
            floodOpacity="0.8"
          />
        </filter>
      </defs>

      {/* Upper eyelid — morphs from flat line to arc */}
      <path
        className="eye-lid-upper"
        stroke={strokeColor}
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />

      {/* Lower eyelid — morphs from flat line to arc */}
      <path
        className="eye-lid-lower"
        stroke={strokeColor}
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />

      {/* Iris ring */}
      <circle
        cx="16"
        cy="16"
        r="4.5"
        stroke={strokeColor}
        strokeWidth="0.8"
        fill="none"
        className="eye-iris"
      />

      {/* Pupil dot with glow */}
      <circle
        cx="16"
        cy="16"
        r="2"
        fill={pupilFill}
        filter={`url(#${id}-glow)`}
        className="eye-pupil"
      />

      {/* Data ingestion particles */}
      {particles.map((p, i) => {
        const start = particleStart(p.angle, p.dist)
        // Linear path from start to center
        const path = `M ${start.x},${start.y} L 16,16`
        return (
          <circle
            key={i}
            r="0.8"
            fill={particleColor}
            className="eye-particle"
            style={{ '--particle-delay': p.delay } as React.CSSProperties}
          >
            <animateMotion
              path={path}
              dur={p.dur}
              begin={p.delay}
              repeatCount="indefinite"
              calcMode="spline"
              keySplines="0.4 0 0.2 1"
              keyTimes="0;1"
            />
            <animate
              attributeName="opacity"
              values="0;0.9;0.9;0"
              keyTimes="0;0.15;0.7;1"
              dur={p.dur}
              begin={p.delay}
              repeatCount="indefinite"
            />
            <animate
              attributeName="r"
              values="0.8;0.8;0.3"
              keyTimes="0;0.6;1"
              dur={p.dur}
              begin={p.delay}
              repeatCount="indefinite"
            />
          </circle>
        )
      })}
    </svg>
  )
}
